/**
 * What the connection is actually doing, from `getStats()`.
 *
 * There was no answer to that question anywhere in this client — `getStats` was
 * not called once. The diagnostics panel that appeared to answer it was a
 * hardcoded object of invented numbers, and it never even rendered: it was gated
 * on `participant.id === "p3"`, an id from demo fixtures that no real meeting
 * produces. So support, and we, have been diagnosing bad calls from nothing.
 *
 * Two decisions worth knowing:
 *
 *  - **It polls only while someone is watching.** Registration is free; the
 *    timer starts with the first subscriber and stops with the last. A stats
 *    loop over every consumer in a large call is real CPU, and it should not be
 *    spent when no panel is open.
 *  - **Rates are deltas, not totals.** `bytesReceived` is cumulative since the
 *    call began, so reporting it — or a loss fraction derived from it — tells
 *    you about the whole meeting rather than about now. A tile that froze
 *    thirty seconds ago still looks healthy by cumulative numbers.
 */
import {
  normalizeReason,
  readDurations,
  worstReason,
  limitedFractions,
  ZERO_DURATIONS,
  type QualityLimitationReason,
  type LimitationDurations,
} from './quality-limitation'

export type RtpDirection = 'inbound' | 'outbound'

export type RtpSnapshot = {
  kind: 'audio' | 'video'
  direction: RtpDirection
  ssrc?: number
  /** Over the last interval, not since the call began. */
  kbps?: number
  /** 0..1, over the last interval. */
  fractionLost?: number
  /**
   * Inbound audio only. 0..1 — the share of what you HEARD that the decoder
   * invented, over the last interval.
   *
   * The most honest measure of audio quality there is, and the one number that
   * survives every argument about it. Packet loss says what the network
   * dropped; concealment says what that actually cost the listener, after FEC,
   * NACK and the decoder's own patching have each had their turn. A call can
   * show 3% loss and conceal almost nothing, or show almost no loss and conceal
   * badly because what it lost arrived too late to be worth anything.
   *
   * Undefined for video, for outbound, and on the first poll of a stream.
   */
  concealedFraction?: number
  jitterMs?: number
  roundTripMs?: number
  frameWidth?: number
  frameHeight?: number
  framesPerSecond?: number
  /** 'VP8', 'H264', 'opus' — from the negotiated parameters, not the report. */
  codec?: string
  /** 'simulcast', 'SVC L3T3', or undefined for a single encoding. */
  mode?: string
  /** Outbound simulcast only: what each layer is actually sending. */
  layers?: { rid?: string; kbps: number; width?: number; height?: number; fps?: number }[]
  /** What the SFU says it is actually sending us — not what we asked for. */
  currentSpatialLayer?: number | null
  currentTemporalLayer?: number | null
  /** mediasoup's 0-10 grade, pushed by the server. */
  score?: number
  producerScore?: number
  /** Outbound video only: why the encoder is holding back, worst across layers. */
  qualityLimitation?: QualityLimitationReason
  /** Share of the last interval spent limited, per reason, 0..1. */
  qualityLimitedFraction?: LimitationDurations
}

export type Registration = {
  kind: 'audio' | 'video'
  direction: RtpDirection
  getStats: () => Promise<RTCStatsReport>
  /** mediasoup's negotiated parameters, for codec and simulcast/SVC mode. */
  rtpParameters?: {
    codecs?: { mimeType?: string }[]
    encodings?: { rid?: string; scalabilityMode?: string }[]
  }
}

type Sample = {
  bytes: number
  packets: number
  lost: number
  /** Inbound audio only; 0 elsewhere. Cumulative counters, differenced below. */
  concealed: number
  samples: number
  at: number
  /** Cumulative seconds per limitation reason; differenced to a fraction below. */
  limited: LimitationDurations
}

const registry = new Map<string, Registration>()
const listeners = new Map<string, Set<(snapshot: RtpSnapshot) => void>>()
const previous = new Map<string, Sample>()

/**
 * Slow enough not to matter, fast enough to watch a layer change land.
 *
 * At one second the numbers jump around more than they inform — the value is
 * already an average over the interval, and halving the interval doubles the
 * cost of collecting it across every tile in the call.
 */
const POLL_MS = 2000

let timer: ReturnType<typeof setInterval> | null = null

function describeCodec(reg: Registration): { codec?: string; mode?: string } {
  const mimeType = reg.rtpParameters?.codecs?.[0]?.mimeType
  const codec = mimeType?.split('/')[1]?.toUpperCase()
  const encodings = reg.rtpParameters?.encodings ?? []
  const scalabilityMode = encodings[0]?.scalabilityMode
  // `L3T3` / `S2T3` — the leading number is the spatial layer count. Only VP9
  // and AV1 carry real SVC here; anything else with several encodings is
  // simulcast, which is a different thing wearing similar words.
  const spatialLayers = Number(scalabilityMode?.match(/^[LS](\d+)/)?.[1] ?? '1')
  const svc = (codec === 'VP9' || codec === 'AV1') && spatialLayers > 1
  const mode = svc ? `SVC ${scalabilityMode}` : encodings.length > 1 ? 'simulcast' : undefined
  return { codec, mode }
}

/** Bytes/packets since the previous poll, or null on the first one. */
function rate(
  key: string,
  sample: Sample,
): { kbps: number; fractionLost: number; concealedFraction: number | null; limited: LimitationDurations } | null {
  const prev = previous.get(key)
  previous.set(key, sample)
  if (!prev) return null
  const seconds = (sample.at - prev.at) / 1000
  if (seconds <= 0) return null

  const bytes = Math.max(0, sample.bytes - prev.bytes)
  const packets = Math.max(0, sample.packets - prev.packets)
  const lost = Math.max(0, sample.lost - prev.lost)
  // Differenced for the same reason everything else here is: cumulative
  // counters describe the whole call, and a stream that concealed heavily for
  // ten seconds a while ago still reads clean by lifetime totals.
  const concealed = Math.max(0, sample.concealed - prev.concealed)
  const samples = Math.max(0, sample.samples - prev.samples)
  return {
    kbps: Math.round((bytes * 8) / seconds / 1000),
    // Against packets ACTUALLY DUE in this window — lost plus arrived. Dividing
    // by arrived alone reads low exactly when things are worst.
    fractionLost: packets + lost > 0 ? lost / (packets + lost) : 0,
    // `totalSamplesReceived` already INCLUDES the concealed ones, so this is a
    // share of what was played rather than of what arrived intact.
    concealedFraction: samples > 0 ? Math.min(1, concealed / samples) : null,
    // Cumulative seconds differenced into "how much of THIS interval was spent
    // limited", which is what answers whether it is struggling now.
    limited: limitedFractions(prev.limited, sample.limited, seconds),
  }
}

function parse(id: string, reg: Registration, report: RTCStatsReport): RtpSnapshot {
  const wanted = reg.direction === 'inbound' ? 'inbound-rtp' : 'outbound-rtp'
  const snapshot: RtpSnapshot = { kind: reg.kind, direction: reg.direction, ...describeCodec(reg) }

  let totalKbps = 0
  let lossWeighted = 0
  let lossWeight = 0
  // Concealment is only meaningful for audio being received; there is no such
  // thing for a sender, and video conceals by holding the last frame instead.
  const wantConcealment = reg.kind === 'audio' && reg.direction === 'inbound'
  let concealedSum = 0
  let concealedSeen = false
  const layers: NonNullable<RtpSnapshot['layers']> = []
  let best: { width?: number; height?: number; fps?: number; pixels: number } | null = null
  // What the far end says it received from us. A sender's own `outbound-rtp`
  // row cannot know about loss or round-trip — only the receiver's report can,
  // and it arrives as `remote-inbound-rtp`. Without this a send-side quality
  // reading is guesswork, which is how the indicator ended up inventing one.
  const remote: { fractionLost?: number; rttMs?: number; jitterMs?: number }[] = []
  // Why the encoder is holding back. Outbound video only — a receiver has no
  // such thing, and audio is never quality-limited in this sense.
  const wantLimitation = reg.kind === 'video' && reg.direction === 'outbound'
  const limitReasons: QualityLimitationReason[] = []
  let limitedFrac: LimitationDurations | null = null

  report.forEach((entry) => {
    const stat = entry as Record<string, unknown>
    const type = stat.type as string

    if (type === 'candidate-pair' && (stat.nominated === true || stat.selected === true)) {
      const rtt = stat.currentRoundTripTime as number | undefined
      if (typeof rtt === 'number') snapshot.roundTripMs = Math.round(rtt * 1000)
      return
    }

    if (type === 'remote-inbound-rtp' && reg.direction === 'outbound') {
      if ((stat.kind as string) !== reg.kind) return
      const fl = stat.fractionLost as number | undefined
      const rtt = stat.roundTripTime as number | undefined
      const jit = stat.jitter as number | undefined
      remote.push({
        fractionLost: typeof fl === 'number' ? fl : undefined,
        rttMs: typeof rtt === 'number' ? Math.round(rtt * 1000) : undefined,
        jitterMs: typeof jit === 'number' ? Math.round(jit * 1000) : undefined,
      })
      return
    }

    if (type !== wanted) return
    if ((stat.kind as string) !== reg.kind) return

    const ssrc = stat.ssrc as number | undefined
    const rid = stat.rid as string | undefined
    // One key per SSRC: an outbound simulcast producer reports a row per layer,
    // and folding them into one running total would make every layer's rate
    // look like the sum of all of them.
    const key = `${id}:${ssrc ?? rid ?? 'single'}`

    const bytes = (reg.direction === 'inbound' ? stat.bytesReceived : stat.bytesSent) as number | undefined
    const packets = (reg.direction === 'inbound' ? stat.packetsReceived : stat.packetsSent) as number | undefined
    const lost = stat.packetsLost as number | undefined
    // Chromium-only, and absent until the first audio actually plays, so both
    // are treated as optional rather than assumed present.
    const concealed = stat.concealedSamples as number | undefined
    const samples = stat.totalSamplesReceived as number | undefined

    const durations = wantLimitation ? readDurations(stat.qualityLimitationDurations) : ZERO_DURATIONS
    if (wantLimitation) limitReasons.push(normalizeReason(stat.qualityLimitationReason))

    const computed = rate(key, {
      bytes: bytes ?? 0,
      packets: packets ?? 0,
      lost: lost ?? 0,
      concealed: concealed ?? 0,
      samples: samples ?? 0,
      at: Date.now(),
      limited: durations,
    })
    // Worst layer wins: the top rung being CPU-limited while the thumbnail is
    // fine is the normal shape of trouble, and averaging would hide it.
    if (computed?.limited) {
      limitedFrac = limitedFrac
        ? {
            cpu: Math.max(limitedFrac.cpu, computed.limited.cpu),
            bandwidth: Math.max(limitedFrac.bandwidth, computed.limited.bandwidth),
            other: Math.max(limitedFrac.other, computed.limited.other),
          }
        : computed.limited
    }

    if (snapshot.ssrc === undefined) snapshot.ssrc = ssrc
    const jitter = stat.jitter as number | undefined
    if (typeof jitter === 'number') snapshot.jitterMs = Math.round(jitter * 1000)

    const width = stat.frameWidth as number | undefined
    const height = stat.frameHeight as number | undefined
    const fps = stat.framesPerSecond as number | undefined

    if (computed) {
      if (wantConcealment && computed.concealedFraction !== null) {
        concealedSum += computed.concealedFraction
        concealedSeen = true
      }
      totalKbps += computed.kbps
      if (packets !== undefined) {
        lossWeighted += computed.fractionLost * (packets + (lost ?? 0))
        lossWeight += packets + (lost ?? 0)
      }
      if (reg.direction === 'outbound') {
        layers.push({ rid, kbps: computed.kbps, width, height, fps })
      }
    }

    // The largest layer currently producing frames is the one worth naming:
    // for a receiver that is simply what it is getting, and for a sender it is
    // the top of the ladder actually being encoded.
    const pixels = (width ?? 0) * (height ?? 0)
    if (pixels > 0 && (!best || pixels > best.pixels)) {
      best = { width, height, fps, pixels }
    }
  })

  snapshot.kbps = totalKbps
  snapshot.fractionLost = lossWeight > 0 ? lossWeighted / lossWeight : 0

  // For a sender, the receiver's report wins over anything we computed locally:
  // our own outbound row counts what we put on the wire, not what arrived.
  // Take the worst across SSRCs rather than an average — one blown simulcast
  // layer is a real problem, and averaging it against two healthy ones hides it.
  if (remote.length > 0) {
    const losses = remote.map((r) => r.fractionLost).filter((v): v is number => typeof v === 'number')
    const rtts = remote.map((r) => r.rttMs).filter((v): v is number => typeof v === 'number')
    const jits = remote.map((r) => r.jitterMs).filter((v): v is number => typeof v === 'number')
    if (losses.length) snapshot.fractionLost = Math.max(...losses)
    // Keep the candidate-pair RTT as the fallback: it is present earlier in a
    // call, before the first receiver report has come back.
    if (rtts.length) snapshot.roundTripMs = Math.max(...rtts)
    if (jits.length) snapshot.jitterMs = Math.max(...jits)
  }
  // Left undefined rather than zero when nothing reported it. Zero means "we
  // measured, and nothing was concealed", which is the opposite of "this
  // browser does not tell us" — and a stat overlay that cannot tell those apart
  // is worse than one that admits it does not know.
  if (concealedSeen) snapshot.concealedFraction = concealedSum
  if (best) {
    const resolved = best as { width?: number; height?: number; fps?: number }
    snapshot.frameWidth = resolved.width
    snapshot.frameHeight = resolved.height
    snapshot.framesPerSecond = resolved.fps === undefined ? undefined : Math.round(resolved.fps)
  }
  if (wantLimitation) {
    snapshot.qualityLimitation = worstReason(limitReasons)
    if (limitedFrac) snapshot.qualityLimitedFraction = limitedFrac
  }

  // Server-known facts last, so they win over anything guessed locally.
  const signal = serverSignals.get(id)
  if (signal) Object.assign(snapshot, signal)

  if (layers.length > 1) {
    snapshot.layers = layers.sort((a, b) => (a.rid ?? '').localeCompare(b.rid ?? ''))
  }

  return snapshot
}

async function poll(): Promise<void> {
  const ids = [...listeners.keys()].filter((id) => (listeners.get(id)?.size ?? 0) > 0)
  await Promise.all(
    ids.map(async (id) => {
      const reg = registry.get(id)
      if (!reg) return
      try {
        const report = await reg.getStats()
        const snapshot = parse(id, reg, report)
        listeners.get(id)?.forEach((listener) => {
          try {
            listener(snapshot)
          } catch {
            // A panel that throws while rendering must not stop the others
            // from updating, or from being polled again next tick.
          }
        })
      } catch {
        // The consumer closed between the check and the call. Nothing to
        // report and nothing to fix.
      }
    }),
  )
}

function ensureTimer(): void {
  const watching = [...listeners.values()].some((set) => set.size > 0)
  if (watching && !timer) {
    void poll()
    timer = setInterval(() => void poll(), POLL_MS)
  } else if (!watching && timer) {
    clearInterval(timer)
    timer = null
  }
}

/**
 * The key a panel asks by.
 *
 * Deliberately NOT the consumer id. A tile knows who it is showing and which
 * source — it has no idea which consumer is carrying it, and threading that id
 * down through the grid would put transport plumbing in a component whose job
 * is to draw a rectangle. `local` is the sender's own side, where there is no
 * consumer at all.
 */
export function statsKey(
  peerId: string | 'local',
  sourceType: string,
  kind: 'audio' | 'video',
): string {
  return `${peerId}:${sourceType}:${kind}`
}

/**
 * The local send-side keys, spelled once.
 *
 * `'local:camera:video'` was written out at four call sites. Any one of them
 * drifting is invisible — the poller simply never sees that id again.
 */
export function localStatsKey(kind: 'audio' | 'video'): string {
  return statsKey('local', 'camera', kind)
}

export const LOCAL_STATS_KEYS = [localStatsKey('audio'), localStatsKey('video')] as const

/**
 * Facts only the SERVER knows, folded into the same snapshot the tile already
 * reads. `getStats()` cannot answer either of these: the browser has no idea
 * which simulcast rung the SFU chose to forward, and mediasoup's 0-10 score
 * exists only on the server.
 */
export type ServerSignal = {
  currentSpatialLayer?: number | null
  currentTemporalLayer?: number | null
  score?: number
  producerScore?: number
}

const serverSignals = new Map<string, ServerSignal>()

/** Merge a server push into whatever we already know about this stream. */
export function setServerSignal(id: string, patch: ServerSignal): void {
  serverSignals.set(id, { ...serverSignals.get(id), ...patch })
}

export function clearServerSignal(id: string): void {
  serverSignals.delete(id)
}

export const rtpStats = {
  /** Free until somebody subscribes — registration alone starts no timer. */
  register(id: string, registration: Registration): void {
    registry.set(id, registration)
  },

  unregister(id: string): void {
    registry.delete(id)
    listeners.delete(id)
    for (const key of [...previous.keys()]) {
      if (key.startsWith(`${id}:`)) previous.delete(key)
    }
    ensureTimer()
  },

  /** Watch one stream. Returns an unsubscribe; the last one out stops the timer. */
  subscribe(id: string, listener: (snapshot: RtpSnapshot) => void): () => void {
    let set = listeners.get(id)
    if (!set) {
      set = new Set()
      listeners.set(id, set)
    }
    set.add(listener)
    ensureTimer()
    return () => {
      set?.delete(listener)
      ensureTimer()
    }
  },

  /** Whether there is anything to watch — the panel offers itself only then. */
  has(id: string): boolean {
    return registry.has(id)
  },

  /** Everything registered. Exists so a watcher can notice keys it is missing. */
  ids(): string[] {
    return [...registry.keys()]
  },

  /**
   * Every registration, for a reader with its own schedule (the traced-meeting
   * report, client-flow-report.ts). Reading them starts no timer, so the panel
   * poller above stays off when nobody is looking.
   */
  entries(): [string, Registration][] {
    return [...registry.entries()]
  },
}

/**
 * What a tile needs in order to name the stream it is showing.
 *
 * Structural, not nominal, so a `Participant` satisfies it without this module
 * importing the UI's model.
 */
export type StatsTile = {
  isLocal?: boolean
  id: string
  sfuPeerId?: string
  sourceKind?: string
  sourceId?: string
  ownerId?: string
  /**
   * sourceType of this tile's primary video consumer, when it is not a camera.
   * 'h323' / 'sip' for a gateway-joined endpoint. Absent means 'camera'.
   */
  videoSourceType?: string
}

/**
 * The one derivation of "which stream is this tile showing".
 *
 * There were three copies of this and they did not agree. The additional-camera
 * branch is why: an extra camera's tile id is `${ownerId}::${sourceId}` and it
 * carries no `sfuPeerId`, so the obvious `sfuPeerId ?? id` yields
 *
 *   "peer-1::aux1"   where the producer registered   "peer-1"
 *
 * giving a key of `peer-1::aux1:aux1:video` against `peer-1:aux1:video`. Nothing
 * throws on a key that misses. The panel simply shows "—" forever, and the layer
 * request simply never fires. Both bugs existed; both were invisible.
 *
 * Returns null when the tile has no stream to name — which is a real answer, not
 * a failure.
 */
export function tileStatsTarget(p: StatsTile): { peer: string; sourceType: string } | null {
  if (p.sourceKind === 'additional') {
    // The slot ('aux1') is the sourceType; the consumer belongs to the OWNER.
    if (!p.sourceId) return null
    if (p.isLocal) return { peer: 'local', sourceType: p.sourceId }
    if (!p.ownerId) return null
    return { peer: p.ownerId, sourceType: p.sourceId }
  }
  // NOT hardcoded 'camera'. A gateway-joined endpoint's consumer registers
  // sourceType 'h323' (or 'sip'), so assuming 'camera' here made this tile
  // report `h323_<ip>_<port>:camera:video` while the consumer lived at
  // `…:h323:video`. Same one-character-apart disagreement as the aux bug
  // above, with a worse consequence: lastN looked up the consumer's key, did
  // not find it in the visible set, decided the tile was off-screen and paused
  // a stream that was on screen the whole time.
  //
  // A local preview has no consumer, so it keeps the literal.
  return {
    peer: p.isLocal ? 'local' : (p.sfuPeerId ?? p.id),
    sourceType: p.isLocal ? 'camera' : (p.videoSourceType || 'camera'),
  }
}

/**
 * Register a locally-published producer under the key its own appData implies.
 *
 * The key is read back out of `producer.appData` rather than rebuilt from the
 * caller's variables, because that is the only way the register side and the
 * produce side cannot disagree — an extra camera's sourceType is a runtime slot
 * ('aux1' / 'aux2'), not a literal, and rebuilding it at the call site is
 * exactly the shape of mistake that left aux tiles blank.
 *
 * Returns the key it used, so callers can log it.
 */
export function registerProducerStats(producer: {
  kind: 'audio' | 'video'
  appData?: { sourceType?: string }
  getStats: () => Promise<RTCStatsReport>
  rtpParameters?: unknown
}): string {
  const sourceType = producer.appData?.sourceType || 'camera'
  const id = statsKey('local', sourceType, producer.kind)
  rtpStats.register(id, {
    kind: producer.kind,
    direction: 'outbound',
    getStats: () => producer.getStats(),
    rtpParameters: producer.rtpParameters as never,
  })
  return id
}

/** The exact inverse, so a stopped share cannot leave `has()` answering yes. */
export function unregisterProducerStats(producer: {
  kind: 'audio' | 'video'
  appData?: { sourceType?: string }
}): string {
  const id = statsKey('local', producer.appData?.sourceType || 'camera', producer.kind)
  rtpStats.unregister(id)
  return id
}
